import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { TIMEOUT_UPLOAD } from '../services/api';
import toast from 'react-hot-toast';
import { Upload, CheckSquare, Square, Users, Sparkles } from 'lucide-react';
import Tooltip from '../components/Tooltip';
import ajuda from '../utils/ajuda';
import { useAuth } from '../context/AuthContext';
import EmailEditor from '../components/EmailEditor';
import { useT } from '../i18n';

// Limites de tamanho por tipo de mídia — alinhados ao que o WhatsApp aceita
const LIMITES_MIDIA = {
  image: 16 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};
const limiteMb = tipo => Math.round((LIMITES_MIDIA[tipo] || LIMITES_MIDIA.image) / (1024 * 1024));

// Definido fora do componente para evitar que React desmonte/remonte
// o input a cada keystroke (nova referência de função = novo tipo de componente)
function Field({ label, name, type = 'text', placeholder = '', tooltipKey = null, value, onChange }) {
  return (
    <div className="field">
      <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {tooltipKey && ajuda[tooltipKey] && <Tooltip texto={ajuda[tooltipKey].texto} exemplo={ajuda[tooltipKey].exemplo} />}
      </label>
      <input name={name} type={type} value={value} onChange={onChange}
        placeholder={placeholder}
        className="input"
      />
    </div>
  );
}

export default function NovaCampanha() {
  const navigate = useNavigate();
  const { isSuperadmin, addonEmail } = useAuth();
  const { t } = useT();
  const [carregando, setCarregando] = useState(false);
  const [etapa, setEtapa] = useState(1);
  const [contatos, setContatos] = useState([]);
  const [contatosSelecionados, setContatosSelecionados] = useState([]);
  const [importando, setImportando] = useState(false);
  const [mostrarFormContato, setMostrarFormContato] = useState(false);
  const [novoContato, setNovoContato] = useState({ nome: '', telefone: '', email: '' });
  const [salvandoContato, setSalvandoContato] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);
  const [instanciasDisponiveis, setInstanciasDisponiveis] = useState([]);
  const [instanciasProprias, setInstanciasProprias] = useState([]);  // só as do tenant (ignora superadmin)
  const [importados, setImportados] = useState([]);
  const [buscaContato, setBuscaContato] = useState('');
  const [filtroInstancia, setFiltroInstancia] = useState('');
  const [midia, setMidia] = useState({ url: '', tipo: 'image', caption: '', origem: 'upload', nomeArquivo: '' });
  const [usarMidia, setUsarMidia] = useState(false);
  const [uploadandoMidia, setUploadandoMidia] = useState(false);
  const [gerandoCopy, setGerandoCopy] = useState(false);
  const [canvaAberto, setCanvaAberto] = useState(false);
  const [chatgptAberto, setChatgptAberto] = useState(false);
  const [chatgptModo, setChatgptModo] = useState(null); // 'imagem' | 'texto'
  const fileInputRef = useRef(null);
  const canvaPopupRef = useRef(null);
  const canvaTimerRef = useRef(null);
  const chatgptPopupRef = useRef(null);
  const chatgptTimerRef = useRef(null);
  const INT_MIN_FLOOR = 15;   // segundos — mínimo anti-ban
  const BLOCO_INT_FLOOR = 120; // segundos — mínimo entre blocos
  const BLOCO_TAM_MAX = 35;    // contatos máximos por bloco

  const [usarIA, setUsarIA] = useState(true);
  const [filtroBusiness, setFiltroBusiness] = useState('todos');
  const [filtroTag, setFiltroTag] = useState('');
  const [detectandoBusiness, setDetectandoBusiness] = useState(false);
  const [jobBusiness, setJobBusiness] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    mensagem: '',
    tipo: 'whatsapp',
    instancia: '',
    instancias: [], // multi-número: array de instâncias selecionadas
    intervalo_min: '',
    bloco_tamanho: '',
    bloco_intervalo: '',
    hora_inicio: '',
    hora_fim: '',
    data_inicio: new Date().toISOString().split('T')[0],
    dias_semana: [1, 2, 3, 4, 5], // seg-sex por padrão; 0=dom ... 6=sáb
    email_assunto: '',
    email_html: ''
  });

  const toggleDiaSemana = (dia) => {
    setForm((f) => {
      const atual = Array.isArray(f.dias_semana) ? f.dias_semana : [];
      const novo = atual.includes(dia)
        ? atual.filter((d) => d !== dia)
        : [...atual, dia].sort((a, b) => a - b);
      return { ...f, dias_semana: novo };
    });
  };

  useEffect(() => {
    api.get('/envio/contatos/listar').then(r => setContatos(r.data));
    api.get('/grupo/listar').then(r => setGrupos(r.data));
    api.get('/whatsapp/instancias').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      const nomes = lista
        .map(i => i?.instance?.instanceName || i?.instanceName || i?.name)
        .filter(Boolean);
      setInstanciasDisponiveis(nomes);
      if (nomes.length > 0) {
        setForm(prev => {
          if (nomes.includes(prev.instancia)) return prev;
          return { ...prev, instancia: nomes[0] || '', instancias: [] };
        });
      }
    }).catch(() => {});
    // ?proprias=1 retorna só instâncias do banco deste tenant (ignora superadmin),
    // usadas para filtrar as pílulas de instância na seleção de contatos.
    api.get('/whatsapp/instancias?proprias=1').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      const nomes = lista.map(i => i?.instanceName || i?.instance?.instanceName || i?.name).filter(Boolean);
      setInstanciasProprias(nomes);
    }).catch(() => {});
  }, []);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function toggleContato(id) {
    setContatosSelecionados(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  }

  async function detectarBusiness() {
    if (detectandoBusiness) return;
    setDetectandoBusiness(true);
    setJobBusiness(null);
    try {
      const { data } = await api.post('/envio/contatos/detectar-business');
      if (data?.total === 0) {
        toast.success(t('nova_campanha.detectar_business_nada_fazer'));
        setDetectandoBusiness(false);
        return;
      }
      toast.success(t('nova_campanha.detectar_business_iniciado', { total: data.total }));
      const interval = setInterval(async () => {
        try {
          const { data: status } = await api.get('/envio/contatos/detectar-business/status');
          setJobBusiness(status);
          if (!status.rodando) {
            clearInterval(interval);
            setDetectandoBusiness(false);
            if (status.erro) {
              toast.error(t('nova_campanha.detectar_business_erro', { erro: status.erro }));
            } else {
              toast.success(t('nova_campanha.detectar_business_concluido', {
                atualizados: status.atualizados,
                total: status.total
              }));
              const r = await api.get('/envio/contatos/listar');
              setContatos(r.data);
            }
          }
        } catch {
          clearInterval(interval);
          setDetectandoBusiness(false);
        }
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.erro || err.message;
      toast.error(t('nova_campanha.detectar_business_erro', { erro: msg }));
      setDetectandoBusiness(false);
    }
  }

  function getContatosFiltrados() {
    const termo = buscaContato.trim().toLowerCase();
    return contatos.filter(c => {
      const passaInstancia = !filtroInstancia || c.instancia === filtroInstancia;
      const passaBusca = !termo ||
        (c.nome && c.nome.toLowerCase().includes(termo)) ||
        c.telefone.includes(termo);
      const passaBusiness =
        filtroBusiness === 'todos' ||
        (filtroBusiness === 'somente_business' && c.is_business === true) ||
        (filtroBusiness === 'somente_pessoal'  && c.is_business !== true);
      const passaTag = !filtroTag || (Array.isArray(c.tags) && c.tags.includes(filtroTag));
      return passaInstancia && passaBusca && passaBusiness && passaTag;
    });
  }

  function selecionarTodos() {
    const visiveis = getContatosFiltrados().map(c => c.id);
    const todosMarcados = visiveis.length > 0 && visiveis.every(id => contatosSelecionados.includes(id));
    if (todosMarcados) {
      setContatosSelecionados(prev => prev.filter(id => !visiveis.includes(id)));
    } else {
      setContatosSelecionados(prev => {
        const set = new Set(prev);
        visiveis.forEach(id => set.add(id));
        return Array.from(set);
      });
    }
  }

  async function handleImportCSV(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    setImportando(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      const { data } = await api.post('/importar/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: TIMEOUT_UPLOAD
      });
      toast.success(t('nova_campanha.toast_csv_importados', { n: data.importados }));
      const r = await api.get('/envio/contatos/listar');
      setContatos(r.data);
      const novosIds = data.contatos.map(c => c.id);
      setImportados(prev => {
        const mapa = new Map(prev.map(c => [c.id, c]));
        data.contatos.forEach(c => mapa.set(c.id, c));
        return Array.from(mapa.values());
      });
      setContatosSelecionados(prev => [
        ...prev,
        ...novosIds.filter(id => !prev.includes(id))
      ]);
    } catch {
      toast.error(t('nova_campanha.toast_csv_erro'));
    } finally {
      setImportando(false);
      e.target.value = '';
    }
  }

  function toggleTodosImportados() {
    const ids = importados.map(c => c.id);
    const todosMarcados = ids.every(id => contatosSelecionados.includes(id));
    if (todosMarcados) {
      setContatosSelecionados(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setContatosSelecionados(prev => {
        const set = new Set(prev);
        ids.forEach(id => set.add(id));
        return Array.from(set);
      });
    }
  }

  async function handleAdicionarContato() {
    if (!novoContato.telefone) { toast.error(t('nova_campanha.toast_contato_telefone_obrigatorio')); return; }
    setSalvandoContato(true);
    try {
      const { data } = await api.post('/envio/contato/adicionar', novoContato);
      const jaExiste = prev => prev.some(c => c.id === data.id);
      toast.success(t('nova_campanha.toast_contato_adicionado'));
      setContatos(prev => jaExiste(prev) ? prev.map(c => c.id === data.id ? data : c) : [data, ...prev]);
      setContatosSelecionados(prev => prev.includes(data.id) ? prev : [...prev, data.id]);
      setNovoContato({ nome: '', telefone: '', email: '' });
      setMostrarFormContato(false);
    } catch (err) {
      const msg = err.response?.data?.erro || err.message || t('nova_campanha.toast_contato_erro');
      toast.error(msg);
    } finally {
      setSalvandoContato(false);
    }
  }

  async function handleSubmit() {
    if (contatosSelecionados.length === 0 && gruposSelecionados.length === 0) {
      toast.error(t('nova_campanha.toast_sem_contatos'));
      return;
    }
    if (usarMidia && !midia.url && midia.origem === 'url') {
      toast.error(t('nova_campanha.toast_midia_url_vazia'));
      return;
    }
    if (form.tipo === 'email' && !form.email_assunto) {
      toast.error(t('nova_campanha.toast_email_sem_assunto'));
      return;
    }
    if (form.tipo === 'whatsapp') {
      const instsSelecionadas = Array.isArray(form.instancias) && form.instancias.length > 0
        ? form.instancias
        : form.instancia ? [form.instancia] : [];
      if (instsSelecionadas.length === 0) {
        toast.error(t('nova_campanha.toast_sem_instancia'));
        return;
      }
      if (instanciasDisponiveis.length > 0 && instsSelecionadas.some(n => !instanciasDisponiveis.includes(n))) {
        toast.error(t('nova_campanha.toast_instancia_invalida'));
        return;
      }
    }
    setCarregando(true);
    try {
      // O máximo é sempre o mínimo + 15s (variação aleatória anti-ban)
      const intervaloBase = Math.max(INT_MIN_FLOOR, Number(form.intervalo_min) || INT_MIN_FLOOR);
      const intervaloMaxCalculado = String(intervaloBase + INT_MIN_FLOOR);
      const instsSelecionadas = Array.isArray(form.instancias) && form.instancias.length > 0
        ? form.instancias
        : form.instancia ? [form.instancia] : [];
      const { data } = await api.post('/campanha/criar-completo', {
        ...form,
        instancia: instsSelecionadas[0] || form.instancia,
        instancias: instsSelecionadas.length > 1 ? instsSelecionadas : undefined,
        intervalo_min: String(intervaloBase),
        intervalo_max: intervaloMaxCalculado,
        bloco_intervalo: String(Math.max(BLOCO_INT_FLOOR, Number(form.bloco_intervalo) || BLOCO_INT_FLOOR)),
        bloco_tamanho: String(Math.min(BLOCO_TAM_MAX, Number(form.bloco_tamanho) || BLOCO_TAM_MAX)),
        contato_ids: contatosSelecionados,
        grupo_ids: gruposSelecionados,
        midia_url: usarMidia ? midia.url : null,
        midia_tipo: usarMidia ? midia.tipo : null,
        midia_caption: usarMidia ? midia.caption : null,
        midia_mimetype: usarMidia ? (midia.mimetype || '') : null,
        midia_nome: usarMidia ? (midia.nomeArquivo || '') : null,
        iniciar_pausada: true,
        usar_ia: usarIA,
        filtro_business: filtroBusiness,
      }, { timeout: 90000 });
      toast.success(t('nova_campanha.toast_campanha_criada'));
      navigate(`/campanhas?novaId=${data.id}`);
    } catch {
      toast.error(t('nova_campanha.toast_campanha_erro'));
    } finally {
      setCarregando(false);
    }
  }

  // URL de templates Instagram Post no Canva (1080×1080) — formato ideal para
  // mensagens de WhatsApp. Sem API key, este é o deep-link público mais próximo.
  const CANVA_URL = 'https://www.canva.com/create/instagram-posts/';

  const abrirCanva = useCallback(() => {
    if (canvaPopupRef.current && !canvaPopupRef.current.closed) {
      canvaPopupRef.current.focus();
      return;
    }
    const largura = 1280, altura = 800;
    const esq = Math.max(0, (window.screen.width - largura) / 2);
    const top = Math.max(0, (window.screen.height - altura) / 2);
    const popup = window.open(
      CANVA_URL,
      'canva_editor',
      `width=${largura},height=${altura},left=${esq},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );
    if (!popup) {
      // Popup bloqueado — abre em nova aba; o usuário usa o botão "Já baixei"
      window.open(CANVA_URL, '_blank');
      setCanvaAberto(true);
      return;
    }
    canvaPopupRef.current = popup;
    setCanvaAberto(true);

    // Monitora o fechamento da popup apenas para resetar o estado visual.
    // Não dispara file picker automático — o usuário aciona via botão.
    canvaTimerRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(canvaTimerRef.current);
        canvaPopupRef.current = null;
      }
    }, 800);
  }, []);

  function cancelarCanva() {
    clearInterval(canvaTimerRef.current);
    if (canvaPopupRef.current && !canvaPopupRef.current.closed) {
      try { canvaPopupRef.current.close(); } catch (_) {}
    }
    canvaPopupRef.current = null;
    setCanvaAberto(false);
  }

  function escolherArquivoCanva() {
    fileInputRef.current?.click();
  }

  // ChatGPT — abre chatgpt.com em popup. Modo "imagem" usa o mesmo fluxo
  // de download+upload do Canva; modo "texto" mantém o popup aberto para o
  // usuário copiar/colar a copy gerada no textarea da mensagem.
  const CHATGPT_URL = 'https://chatgpt.com/';

  const abrirChatGpt = useCallback((modo) => {
    if (chatgptPopupRef.current && !chatgptPopupRef.current.closed) {
      chatgptPopupRef.current.focus();
      setChatgptModo(modo);
      setChatgptAberto(true);
      return;
    }
    const largura = 1280, altura = 800;
    const esq = Math.max(0, (window.screen.width - largura) / 2);
    const top = Math.max(0, (window.screen.height - altura) / 2);
    const popup = window.open(
      CHATGPT_URL,
      'chatgpt_editor',
      `width=${largura},height=${altura},left=${esq},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );
    if (!popup) {
      window.open(CHATGPT_URL, '_blank');
      setChatgptModo(modo);
      setChatgptAberto(true);
      return;
    }
    chatgptPopupRef.current = popup;
    setChatgptModo(modo);
    setChatgptAberto(true);

    chatgptTimerRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(chatgptTimerRef.current);
        chatgptPopupRef.current = null;
      }
    }, 800);
  }, []);

  function cancelarChatGpt() {
    clearInterval(chatgptTimerRef.current);
    if (chatgptPopupRef.current && !chatgptPopupRef.current.closed) {
      try { chatgptPopupRef.current.close(); } catch (_) {}
    }
    chatgptPopupRef.current = null;
    setChatgptAberto(false);
    setChatgptModo(null);
  }

  function escolherArquivoChatGpt() {
    fileInputRef.current?.click();
  }

  // Limpa os timers ao desmontar
  useEffect(() => () => {
    clearInterval(canvaTimerRef.current);
    clearInterval(chatgptTimerRef.current);
  }, []);

  async function handleUploadMidia(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    const limite = LIMITES_MIDIA[midia.tipo] || LIMITES_MIDIA.image;
    if (arquivo.size > limite) {
      toast.error(t('nova_campanha.toast_midia_grande', {
        tipo: t(`nova_campanha.midia_tipo_${midia.tipo}`),
        max: limiteMb(midia.tipo),
      }));
      e.target.value = '';
      return;
    }
    setUploadandoMidia(true);
    try {
      const formData = new FormData();
      formData.append('tipo', midia.tipo);
      formData.append('arquivo', arquivo);
      const { data } = await api.post('/upload/midia', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setMidia(prev => ({ ...prev, url: data.url, nomeArquivo: data.nome, mimetype: data.mimetype || '' }));
      // Encerra o fluxo do Canva caso estivesse ativo
      if (canvaPopupRef.current && !canvaPopupRef.current.closed) {
        try { canvaPopupRef.current.close(); } catch (_) {}
      }
      clearInterval(canvaTimerRef.current);
      canvaPopupRef.current = null;
      setCanvaAberto(false);
      // Mesmo tratamento para o popup do ChatGPT (modo imagem)
      if (chatgptModo === 'imagem' && chatgptPopupRef.current && !chatgptPopupRef.current.closed) {
        try { chatgptPopupRef.current.close(); } catch (_) {}
        clearInterval(chatgptTimerRef.current);
        chatgptPopupRef.current = null;
        setChatgptAberto(false);
        setChatgptModo(null);
      }
      toast.success(t('nova_campanha.toast_midia_enviada'));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('nova_campanha.toast_midia_erro'));
    } finally {
      setUploadandoMidia(false);
      e.target.value = '';
    }
  }

  async function handleGerarCopy() {
    if (!midia.url || midia.tipo !== 'image') return;
    setGerandoCopy(true);
    try {
      const { data } = await api.post('/campanha/gerar-copy-ia', { imagem_url: midia.url });
      setForm(prev => ({ ...prev, mensagem: data.copy }));
      toast.success(t('nova_campanha.toast_copy_gerada'));
    } catch {
      toast.error(t('nova_campanha.toast_copy_erro'));
    } finally {
      setGerandoCopy(false);
    }
  }

  const contatosFiltrados = getContatosFiltrados();

  // Tags únicas presentes nos contatos (respeitando filtro de instância)
  const tagsDisponiveis = [...new Set(
    (filtroInstancia ? contatos.filter(c => c.instancia === filtroInstancia) : contatos)
      .flatMap(c => Array.isArray(c.tags) ? c.tags : [])
  )].sort();
  const todosSelecionados = contatosFiltrados.length > 0 && contatosFiltrados.every(c => contatosSelecionados.includes(c.id));
  // Pílulas de instância: só mostra as que realmente pertencem a este tenant
  // (vindas de /whatsapp/instancias). Evita exibir instâncias de outro usuário
  // (vínculo antigo em contatos) ou que já não existem mais na Evolution — sem
  // remover nada do banco. Fallback: se a lista ainda não carregou, mantém o
  // comportamento anterior (mostra todas as encontradas nos contatos).
  const instanciasNosContatos = (() => {
    const dosContatos = [...new Set(contatos.map(c => c.instancia).filter(Boolean))];
    // Usa instanciasProprias (só deste tenant, ignora superadmin) para o filtro.
    // Fallback para a lista completa se ainda não carregou.
    const referencia = instanciasProprias.length ? instanciasProprias : instanciasDisponiveis;
    if (!referencia.length) return dosContatos;
    const norm = s => String(s).trim().toLowerCase();
    const validas = new Set(referencia.map(norm));
    return dosContatos.filter(inst => validas.has(norm(inst)));
  })();
  const totalSelecionados = contatosSelecionados.length + gruposSelecionados.reduce((acc, gid) => {
    const g = grupos.find(x => x.id === gid);
    return acc + (g?.total_contatos || 0);
  }, 0);

  const isEmail = form.tipo === 'email';

  const sectionTitle = (icon, title, hint) => (
    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)', margin: '0.5rem 0 0.5rem' }}>
      {icon} {title}
      {hint && <span className="muted" style={{ fontWeight: 400, fontSize: '12px', marginLeft: '6px' }}>{hint}</span>}
    </p>
  );

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('nova_campanha.titulo')}</h1>
          <p className="page-h-sub">{t('nova_campanha.subtitulo')}</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="row" style={{ marginBottom: '1.5rem' }}>
        {[t('nova_campanha.step_configurar'), t('nova_campanha.step_contatos')].map((label, i) => (
          <div key={i} onClick={() => setEtapa(i + 1)}
            className={`pill${etapa === i + 1 ? ' is-active' : ''}`}
            style={etapa === i + 1
              ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff' }
              : undefined}
          >
            {i + 1}. {label}
          </div>
        ))}
      </div>

      {etapa === 1 && (
        <div className="card card-pad-lg" style={{ maxWidth: '660px' }}>
          <Field label={t('nova_campanha.nome_campanha_label')} name="nome" placeholder={t('nova_campanha.nome_campanha_placeholder')} tooltipKey="nome_campanha" value={form.nome} onChange={handleChange} />

          {/* Tipo de envio */}
          <div className="field">
            <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
              {t('nova_campanha.tipo_envio_label')}
              <Tooltip texto={ajuda.tipo_envio.texto} exemplo={ajuda.tipo_envio.exemplo} />
            </label>
            <select name="tipo" value={form.tipo} onChange={handleChange} className="select">
              <option value="whatsapp">{t('nova_campanha.tipo_whatsapp')}</option>
              {(addonEmail || isSuperadmin) && <option value="email">{t('nova_campanha.tipo_email')}</option>}
            </select>
            {!addonEmail && !isSuperadmin && (
              <p className="field-help">
                {t('nova_campanha.addon_email_hint')}
              </p>
            )}
          </div>

          {/* Campos WhatsApp */}
          {!isEmail && (
            <>
              <div className="field">
                <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
                  {t('nova_campanha.instancia_label')}
                  {ajuda.instancia_whatsapp && <Tooltip texto={ajuda.instancia_whatsapp.texto} exemplo={ajuda.instancia_whatsapp.exemplo} />}
                </label>
                {instanciasDisponiveis.length > 0 ? (
                  instanciasDisponiveis.length === 1 ? (
                    <select name="instancia" value={form.instancia} onChange={handleChange} className="select">
                      {instanciasDisponiveis.map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {instanciasDisponiveis.map(n => {
                        const selecionada = (form.instancias || []).includes(n) || (!(form.instancias || []).length && form.instancia === n);
                        return (
                          <label key={n} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--color-text)' }}>
                            <input
                              type="checkbox"
                              checked={selecionada}
                              onChange={e => {
                                const atual = Array.isArray(form.instancias) && form.instancias.length > 0
                                  ? form.instancias
                                  : form.instancia ? [form.instancia] : [];
                                const novo = e.target.checked
                                  ? [...new Set([...atual, n])]
                                  : atual.filter(x => x !== n);
                                setForm(f => ({ ...f, instancias: novo, instancia: novo[0] || '' }));
                              }}
                              style={{ accentColor: 'var(--color-accent)', width: '15px', height: '15px', flexShrink: 0 }}
                            />
                            {n}
                          </label>
                        );
                      })}
                      {(form.instancias || []).length > 1 && (
                        <p className="field-help" style={{ color: 'var(--color-accent)', marginTop: '4px' }}>
                          {(form.instancias || []).length} números selecionados — contatos distribuídos em round-robin.
                        </p>
                      )}
                    </div>
                  )
                ) : (
                  <>
                    <input name="instancia" type="text" value={form.instancia} onChange={handleChange}
                      placeholder={t('nova_campanha.instancia_placeholder')}
                      className="input"
                    />
                    <p className="field-help">
                      {t('nova_campanha.instancia_nenhuma')} <a href="/whatsapp" style={{ color: 'var(--color-accent)' }}>{t('nova_campanha.instancia_criar_link')}</a>.
                    </p>
                  </>
                )}
              </div>

              {/* Mídia — movida para antes da mensagem */}
              <div style={{ marginBottom: '1rem' }}>
                <div className="row" style={{ marginBottom: '10px' }}>
                  <input type="checkbox" id="usarMidia" checked={usarMidia}
                    onChange={e => setUsarMidia(e.target.checked)}
                    style={{ accentColor: 'var(--color-accent)', width: '15px', height: '15px' }}
                  />
                  <label htmlFor="usarMidia" style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    {t('nova_campanha.midia_checkbox')}
                    <Tooltip texto={ajuda.midia.texto} exemplo={ajuda.midia.exemplo} />
                  </label>
                </div>

                {usarMidia && (
                  <div style={{ background: '#f8faff', borderRadius: 'var(--radius)', padding: '14px', border: '1px solid #bfdbfe' }}>
                    <div className="field">
                      <label className="label">{t('nova_campanha.midia_tipo_label')}</label>
                      <select value={midia.tipo} onChange={e => setMidia({ ...midia, tipo: e.target.value })} className="select">
                        <option value="image">{t('nova_campanha.midia_tipo_image')}</option>
                        <option value="video">{t('nova_campanha.midia_tipo_video')}</option>
                        <option value="audio">{t('nova_campanha.midia_tipo_audio')}</option>
                        <option value="document">{t('nova_campanha.midia_tipo_document')}</option>
                      </select>
                    </div>

                    <div className="row" style={{ marginBottom: '1rem' }}>
                      {['upload', 'url'].map(op => (
                        <button key={op} type="button"
                          onClick={() => setMidia({ ...midia, origem: op })}
                          className={`pill${(midia.origem || 'upload') === op ? ' is-active' : ''}`}
                        >
                          {op === 'upload' ? t('nova_campanha.midia_origem_upload') : t('nova_campanha.midia_origem_url')}
                        </button>
                      ))}
                    </div>

                    {(midia.origem || 'upload') === 'upload' && (
                      <div>
                        {/* ── Upload local ── */}
                        <label style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          padding: '20px', borderRadius: 'var(--radius)', border: '2px dashed #bfdbfe',
                          background: '#fff', cursor: 'pointer', fontSize: '13px', color: 'var(--color-accent)'
                        }}>
                          <input ref={fileInputRef} type="file"
                            accept={midia.tipo === 'image' ? 'image/*' : midia.tipo === 'video' ? 'video/*' : midia.tipo === 'audio' ? 'audio/*' : '*/*'}
                            onChange={handleUploadMidia} style={{ display: 'none' }}
                          />
                          {uploadandoMidia ? t('nova_campanha.midia_upload_enviando') :
                            midia.url ? t('nova_campanha.midia_upload_enviado', { nome: midia.nomeArquivo || 'Arquivo enviado' }) :
                            t('nova_campanha.midia_upload_cta')}
                        </label>

                        {/* ── Botões de criação de imagem: Canva + ChatGPT ── */}
                        {midia.tipo === 'image' && !midia.url && (
                          <div style={{ marginTop: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t('nova_campanha.canva_divider')}</span>
                              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                            </div>

                            {!canvaAberto && !(chatgptAberto && chatgptModo === 'imagem') && (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={abrirCanva}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                    flex: 1, padding: '10px 12px',
                                    borderRadius: 'var(--radius)',
                                    border: '1.5px solid #13305f',
                                    background: '#faf5ff',
                                    color: '#13305f',
                                    fontSize: '13px', fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  <Sparkles size={15} />
                                  {t('nova_campanha.canva_cta')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => abrirChatGpt('imagem')}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                    flex: 1, padding: '10px 12px',
                                    borderRadius: 'var(--radius)',
                                    border: '1.5px solid #10a37f',
                                    background: '#f0fdf9',
                                    color: '#0d8c6c',
                                    fontSize: '13px', fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  <Sparkles size={15} />
                                  {t('nova_campanha.chatgpt_cta_imagem')}
                                </button>
                              </div>
                            )}

                            {canvaAberto && (
                              <div style={{ padding: '12px', borderRadius: 'var(--radius)', border: '1.5px solid #ddd6fe', background: '#faf5ff' }}>
                                <p style={{ fontSize: '12px', color: '#6d28d9', margin: '0 0 10px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Sparkles size={14} /> {t('nova_campanha.canva_aguardando')}
                                </p>
                                <ol style={{ fontSize: '11px', color: '#5b21b6', margin: '0 0 10px', paddingLeft: '18px', lineHeight: 1.6 }}>
                                  <li>{t('nova_campanha.canva_passos_1')}</li>
                                  <li>{t('nova_campanha.canva_passos_2')}</li>
                                  <li>{t('nova_campanha.canva_passos_3')}</li>
                                </ol>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button type="button" onClick={escolherArquivoCanva}
                                    style={{
                                      flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
                                      border: 'none', background: '#13305f', color: '#fff',
                                      fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                    }}>
                                    {t('nova_campanha.canva_ja_baixei')}
                                  </button>
                                  <button type="button" onClick={cancelarCanva}
                                    style={{
                                      padding: '8px 12px', borderRadius: 'var(--radius)',
                                      border: '1px solid #d4d4d8', background: '#fff', color: '#52525b',
                                      fontSize: '12px', cursor: 'pointer',
                                    }}>
                                    {t('nova_campanha.canva_cancelar')}
                                  </button>
                                </div>
                              </div>
                            )}

                            {chatgptAberto && chatgptModo === 'imagem' && (
                              <div style={{ padding: '12px', borderRadius: 'var(--radius)', border: '1.5px solid #a7f3d0', background: '#f0fdf9' }}>
                                <p style={{ fontSize: '12px', color: '#0d8c6c', margin: '0 0 10px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Sparkles size={14} /> {t('nova_campanha.chatgpt_aguardando_imagem')}
                                </p>
                                <ol style={{ fontSize: '11px', color: '#065f46', margin: '0 0 10px', paddingLeft: '18px', lineHeight: 1.6 }}>
                                  <li>{t('nova_campanha.chatgpt_passos_1_imagem')}</li>
                                  <li>{t('nova_campanha.chatgpt_passos_2_imagem')}</li>
                                  <li>{t('nova_campanha.chatgpt_passos_3_imagem')}</li>
                                </ol>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button type="button" onClick={escolherArquivoChatGpt}
                                    style={{
                                      flex: 1, padding: '8px 12px', borderRadius: 'var(--radius)',
                                      border: 'none', background: '#10a37f', color: '#fff',
                                      fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                    }}>
                                    {t('nova_campanha.chatgpt_ja_baixei')}
                                  </button>
                                  <button type="button" onClick={cancelarChatGpt}
                                    style={{
                                      padding: '8px 12px', borderRadius: 'var(--radius)',
                                      border: '1px solid #d4d4d8', background: '#fff', color: '#52525b',
                                      fontSize: '12px', cursor: 'pointer',
                                    }}>
                                    {t('nova_campanha.chatgpt_cancelar')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <p className="field-help">{t('nova_campanha.midia_limite_hint', { max: limiteMb(midia.tipo) })}</p>
                        {midia.url && midia.tipo === 'image' && (
                          <div style={{ marginTop: '8px', position: 'relative', display: 'inline-block' }}>
                            <img src={midia.url} alt="preview" onError={e => e.target.style.display = 'none'}
                              style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)', display: 'block' }}
                            />
                            <button type="button"
                              onClick={() => setMidia(prev => ({ ...prev, url: '', nomeArquivo: '', mimetype: '' }))}
                              style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: '20px', height: '20px', color: '#fff', fontSize: '12px', cursor: 'pointer', lineHeight: '20px', textAlign: 'center', padding: 0 }}
                            >×</button>
                          </div>
                        )}
                      </div>
                    )}

                    {midia.origem === 'url' && (
                      <div>
                        <label className="label">{t('nova_campanha.midia_url_label')}</label>
                        <input value={midia.url} onChange={e => setMidia({ ...midia, url: e.target.value })}
                          placeholder={t('nova_campanha.midia_url_placeholder')}
                          className="input"
                        />
                        {midia.url && midia.tipo === 'image' && (
                          <img src={midia.url} alt="preview" onError={e => e.target.style.display = 'none'}
                            style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)', marginTop: '8px' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mensagem + botão gerar copy com IA */}
              <div className="field">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label className="label" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                    {t('nova_campanha.mensagem_label')}
                    <Tooltip texto={ajuda.mensagem.texto} exemplo={ajuda.mensagem.exemplo} />
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {(() => {
                      const podeGerar = usarMidia && midia.tipo === 'image' && !!midia.url;
                      return (
                        <button
                          type="button"
                          onClick={handleGerarCopy}
                          disabled={!podeGerar || gerandoCopy}
                          title={podeGerar ? t('nova_campanha.gerar_copy_title_habilitado') : t('nova_campanha.gerar_copy_title_desabilitado')}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '5px',
                            fontSize: '12px', fontWeight: 600, padding: '4px 10px',
                            borderRadius: 'var(--radius-sm)', border: 'none', cursor: podeGerar ? 'pointer' : 'not-allowed',
                            background: podeGerar ? 'linear-gradient(135deg,#16a34a,#13305f)' : 'var(--color-bg-soft)',
                            color: podeGerar ? '#fff' : 'var(--color-text-muted)',
                            opacity: gerandoCopy ? 0.7 : 1,
                            transition: 'all 0.2s',
                          }}
                        >
                          <Sparkles size={13} />
                          {gerandoCopy ? t('nova_campanha.gerar_copy_gerando') : t('nova_campanha.gerar_copy_btn')}
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={() => abrirChatGpt('texto')}
                      title={t('nova_campanha.chatgpt_cta_texto_title')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        fontSize: '12px', fontWeight: 600, padding: '4px 10px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid #10a37f',
                        background: '#fff', color: '#0d8c6c',
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      <Sparkles size={13} />
                      {t('nova_campanha.chatgpt_cta_texto')}
                    </button>
                  </div>
                </div>
                {chatgptAberto && chatgptModo === 'texto' && (
                  <div style={{ marginBottom: '8px', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid #a7f3d0', background: '#f0fdf9', fontSize: '12px', color: '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Sparkles size={13} /> {t('nova_campanha.chatgpt_aguardando_texto')}
                    </span>
                    <button type="button" onClick={cancelarChatGpt}
                      style={{
                        padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                        border: '1px solid #d4d4d8', background: '#fff', color: '#52525b',
                        fontSize: '11px', cursor: 'pointer',
                      }}>
                      {t('nova_campanha.chatgpt_cancelar')}
                    </button>
                  </div>
                )}
                <textarea name="mensagem" value={form.mensagem} onChange={handleChange}
                  placeholder={t('nova_campanha.mensagem_placeholder')} rows={4}
                  className="textarea"
                />
                <p className="field-help">{t('nova_campanha.mensagem_hint')}</p>

                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 12,
                  padding: '10px 12px', borderRadius: 'var(--radius)',
                  background: usarIA ? 'var(--color-accent-soft)' : 'var(--color-bg-soft)',
                  border: `1px solid ${usarIA ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  cursor: 'pointer', transition: 'background 150ms, border-color 150ms'
                }}>
                  <input
                    type="checkbox"
                    checked={usarIA}
                    onChange={e => {
                      const checked = e.target.checked;
                      setUsarIA(checked);
                      if (!checked) {
                        setForm(prev => ({
                          ...prev,
                          intervalo_min: (!prev.intervalo_min || Number(prev.intervalo_min) < INT_MIN_FLOOR) ? String(INT_MIN_FLOOR) : prev.intervalo_min,
                          bloco_tamanho: (!prev.bloco_tamanho || Number(prev.bloco_tamanho) === 0) ? String(BLOCO_TAM_MAX) : prev.bloco_tamanho,
                          bloco_intervalo: (!prev.bloco_intervalo || Number(prev.bloco_intervalo) < BLOCO_INT_FLOOR) ? String(BLOCO_INT_FLOOR) : prev.bloco_intervalo,
                        }));
                      }
                    }}
                    style={{ marginTop: 3, accentColor: 'var(--color-accent)' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
                      {t('nova_campanha.ia_titulo')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                      {usarIA
                        ? t('nova_campanha.ia_ativo')
                        : t('nova_campanha.ia_inativo')}
                    </div>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Campos Email */}
          {isEmail && (
            <>
              <div className="field">
                <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
                  {t('nova_campanha.email_assunto_label')}
                  <Tooltip texto={t('nova_campanha.email_assunto_tooltip')} exemplo={t('nova_campanha.email_assunto_tooltip_exemplo')} />
                </label>
                <input name="email_assunto" value={form.email_assunto} onChange={handleChange}
                  placeholder={t('nova_campanha.email_assunto_placeholder')}
                  className="input"
                />
              </div>

              <div className="field">
                <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
                  {t('nova_campanha.email_fallback_label')}
                  <Tooltip texto={t('nova_campanha.email_fallback_tooltip')} exemplo={t('nova_campanha.email_fallback_tooltip_exemplo')} />
                </label>
                <textarea name="mensagem" value={form.mensagem} onChange={handleChange}
                  placeholder={t('nova_campanha.email_fallback_placeholder')} rows={3}
                  className="textarea"
                />
              </div>

              <div className="field">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label className="label" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
                    {t('nova_campanha.email_corpo_label')}
                    <Tooltip texto={t('nova_campanha.email_corpo_tooltip')} exemplo={t('nova_campanha.email_corpo_tooltip_exemplo')} />
                  </label>
                  <button type="button" onClick={() => {
                    if (!form.email_html) {
                      setForm({ ...form, email_html: `<div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">
  <div style="background:#16a34a;padding:30px;text-align:center;border-radius:12px 12px 0 0;">
    <h1 style="color:#fff;margin:0;">Sua Empresa</h1>
  </div>
  <div style="padding:30px;">
    <h2>Olá {nome}! 👋</h2>
    <p style="color:#555;line-height:1.7;">Temos uma novidade especial para você. Confira abaixo!</p>
    <div style="text-align:center;margin:30px 0;">
      <a href="#" style="background:#16a34a;color:#fff;padding:14px 32px;border-radius:8px;text-decoration:none;font-weight:600;">Ver oferta →</a>
    </div>
    <p style="color:#888;font-size:13px;">Abraços,<br>Equipe Sua Empresa</p>
  </div>
</div>` });
                    }
                  }} className="btn btn-secondary btn-sm">
                    {t('nova_campanha.email_usar_template')}
                  </button>
                </div>

                <EmailEditor
                  value={form.email_html}
                  onChange={(html) => setForm(prev => ({ ...prev, email_html: html }))}
                  onUploadImage={async (arquivo) => {
                    const formData = new FormData();
                    formData.append('arquivo', arquivo);
                    const { data } = await api.post('/upload/midia', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                      timeout: 180000,
                    });
                    return data.url;
                  }}
                />

                <p className="field-help">
                  {t('nova_campanha.email_hint')}
                </p>
              </div>

              {/* Preview do email */}
              {form.email_html && (
                <div className="field">
                  <label className="label">{t('nova_campanha.email_preview_label')}</label>
                  <div style={{
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden',
                    maxHeight: '400px', overflowY: 'auto', background: '#fff'
                  }}>
                    <div style={{ padding: '12px 16px', background: 'var(--color-bg-soft)', borderBottom: '1px solid var(--color-border)', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      <strong>{t('nova_campanha.email_preview_de')}</strong> seu-email@empresa.com &nbsp;|&nbsp;
                      <strong>{t('nova_campanha.email_preview_assunto')}</strong> {form.email_assunto || t('nova_campanha.email_preview_sem_assunto')}
                    </div>
                    <div
                      style={{ padding: '16px' }}
                      dangerouslySetInnerHTML={{
                        __html: form.email_html
                          .replace(/\{nome\}/g, 'João Silva')
                          .replace(/\{telefone\}/g, '5511999999999')
                          .replace(/\{email\}/g, 'joao@email.com')
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Intervalo entre mensagens */}
          {sectionTitle('⏱️', t('nova_campanha.intervalo_secao', { canal: isEmail ? t('nova_campanha.intervalo_canal_emails') : t('nova_campanha.intervalo_canal_mensagens') }), t('nova_campanha.intervalo_hint_opcional'))}
          <div className="field-row">
            <Field
              label={t('nova_campanha.intervalo_min_label')}
              name="intervalo_min"
              type="number"
              placeholder={String(INT_MIN_FLOOR)}
              tooltipKey="intervalo_min"
              value={form.intervalo_min}
              onChange={e => {
                const v = e.target.value;
                setForm(prev => ({ ...prev, intervalo_min: v && Number(v) < INT_MIN_FLOOR ? String(INT_MIN_FLOOR) : v }));
              }}
              min={INT_MIN_FLOOR}
            />
          </div>
          <p className="field-help" style={{ marginBottom: '1rem' }}>
            {(() => {
              const base = Math.max(INT_MIN_FLOOR, Number(form.intervalo_min) || INT_MIN_FLOOR);
              return t('nova_campanha.intervalo_descricao_com_valor', { max: `${base + INT_MIN_FLOOR}s` });
            })()}
            {' '}<span style={{ color: '#ef4444', fontSize: 11 }}>mín. {INT_MIN_FLOOR}s</span>
          </p>

          {/* Envio em blocos */}
          {sectionTitle('📦', t('nova_campanha.blocos_secao'), t('nova_campanha.blocos_hint_opcional'))}
          <div className="field-row">
            <Field
              label={t('nova_campanha.blocos_tamanho_label')}
              name="bloco_tamanho"
              type="number"
              placeholder={String(BLOCO_TAM_MAX)}
              value={form.bloco_tamanho}
              onChange={e => {
                const v = e.target.value;
                setForm(prev => ({ ...prev, bloco_tamanho: v && Number(v) > BLOCO_TAM_MAX ? String(BLOCO_TAM_MAX) : v }));
              }}
              max={BLOCO_TAM_MAX}
            />
            <Field
              label={t('nova_campanha.blocos_intervalo_label')}
              name="bloco_intervalo"
              type="number"
              placeholder={String(BLOCO_INT_FLOOR)}
              value={form.bloco_intervalo}
              onChange={e => {
                const v = e.target.value;
                setForm(prev => ({ ...prev, bloco_intervalo: v && Number(v) < BLOCO_INT_FLOOR ? String(BLOCO_INT_FLOOR) : v }));
              }}
              min={BLOCO_INT_FLOOR}
            />
          </div>
          <p className="field-help" style={{ marginBottom: '1rem' }}>
            {t('nova_campanha.blocos_descricao', { n: form.bloco_tamanho || BLOCO_TAM_MAX, x: form.bloco_intervalo || BLOCO_INT_FLOOR })}
            {' '}<span style={{ color: '#ef4444', fontSize: 11 }}>pausa mín. {BLOCO_INT_FLOOR}s</span>
          </p>

          {/* Janela de envio */}
          {sectionTitle('🕐', t('nova_campanha.janela_secao'), t('nova_campanha.janela_hint_opcional'))}
          <div className="field-row field-row-3">
            <Field label={t('nova_campanha.janela_hora_inicio_label')} name="hora_inicio" type="time" tooltipKey="hora_inicio" value={form.hora_inicio} onChange={handleChange} />
            <Field label={t('nova_campanha.janela_hora_fim_label')} name="hora_fim" type="time" tooltipKey="hora_fim" value={form.hora_fim} onChange={handleChange} />
            <Field label={t('nova_campanha.janela_data_inicio_label')} name="data_inicio" type="date" tooltipKey="data_inicio" value={form.data_inicio} onChange={handleChange} />
          </div>

          <div className="field" style={{ marginTop: '0.5rem' }}>
            <label className="label">{t('nova_campanha.janela_dias_label')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
              {[
                { dia: 1, key: 'dia_seg' },
                { dia: 2, key: 'dia_ter' },
                { dia: 3, key: 'dia_qua' },
                { dia: 4, key: 'dia_qui' },
                { dia: 5, key: 'dia_sex' },
                { dia: 6, key: 'dia_sab' },
                { dia: 0, key: 'dia_dom' },
              ].map(({ dia, key }) => {
                const ativo = Array.isArray(form.dias_semana) && form.dias_semana.includes(dia);
                return (
                  <button
                    key={dia}
                    type="button"
                    onClick={() => toggleDiaSemana(dia)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: ativo ? '1.5px solid #16a34a' : '1.5px solid #ddd',
                      background: ativo ? '#16a34a' : '#fff',
                      color: ativo ? '#fff' : '#555',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {t(`nova_campanha.${key}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="field-help" style={{ marginBottom: '1.5rem' }}>
            {t('nova_campanha.janela_descricao')}
          </p>

          <button onClick={() => {
            if (!form.nome) { toast.error(t('nova_campanha.validacao_nome')); return; }
            if (!isEmail && !form.mensagem) { toast.error(t('nova_campanha.validacao_mensagem')); return; }
            if (isEmail && !form.email_assunto) { toast.error(t('nova_campanha.validacao_email_assunto')); return; }
            if (isEmail && !form.email_html && !form.mensagem) { toast.error(t('nova_campanha.validacao_email_corpo')); return; }
            if (form.intervalo_min && Number(form.intervalo_min) < INT_MIN_FLOOR) {
              toast.error(t('nova_campanha.validacao_intervalo_minimo'));
              return;
            }
            if (!usarIA && (!form.bloco_tamanho || !form.bloco_intervalo)) {
              toast.error(t('nova_campanha.validacao_blocos_sem_ia'));
              return;
            }
            setEtapa(2);
          }} className="btn btn-primary btn-block" style={{ padding: '12px' }}>
            {t('nova_campanha.btn_proximo')}
          </button>
        </div>
      )}

      {etapa === 2 && (
        <div className="card card-pad-lg" style={{ maxWidth: '760px' }}>

          {grupos.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Users size={14} /> {t('nova_campanha.grupos_label')}
              </p>
              <div className="row">
                {grupos.map(g => (
                  <div key={g.id}
                    onClick={() => setGruposSelecionados(prev =>
                      prev.includes(g.id) ? prev.filter(x => x !== g.id) : [...prev, g.id]
                    )}
                    className={`pill${gruposSelecionados.includes(g.id) ? ' is-active' : ''}`}
                  >
                    {g.nome} · {g.total_contatos}
                    {g.instancia && (
                      <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.7 }}>({g.instancia})</span>
                    )}
                  </div>
                ))}
              </div>
              <hr style={{ margin: '1.25rem 0' }} />
            </div>
          )}

          {/* Filtro por instância */}
          {instanciasNosContatos.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('nova_campanha.filtrar_instancia')}
              </p>
              <div className="row" style={{ flexWrap: 'wrap', gap: '6px' }}>
                <button
                  onClick={() => setFiltroInstancia('')}
                  className={`pill${!filtroInstancia ? ' is-active' : ''}`}
                  style={!filtroInstancia ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff' } : undefined}
                >
                  {t('nova_campanha.filtrar_todas')}
                </button>
                {instanciasNosContatos.map(inst => {
                  const qtd = contatos.filter(c => c.instancia === inst).length;
                  const ativo = filtroInstancia === inst;
                  return (
                    <button
                      key={inst}
                      onClick={() => setFiltroInstancia(prev => prev === inst ? '' : inst)}
                      className={`pill${ativo ? ' is-active' : ''}`}
                      style={ativo ? { background: '#13305f', borderColor: '#13305f', color: '#fff' } : { borderColor: '#86efac', color: '#13305f' }}
                    >
                      📲 {inst}
                      <span style={{ marginLeft: '4px', opacity: 0.75, fontSize: '11px' }}>({qtd})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filtro por tipo de conta (Business / Pessoal) */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', gap: '8px', flexWrap: 'wrap' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('nova_campanha.filtro_business_secao')}
              </p>
              <button
                type="button"
                onClick={detectarBusiness}
                disabled={detectandoBusiness}
                className="btn btn-secondary btn-sm"
                title={t('nova_campanha.detectar_business_hint')}
              >
                {detectandoBusiness
                  ? t('nova_campanha.detectar_business_em_andamento', { processados: jobBusiness?.processados || 0, total: jobBusiness?.total || 0 })
                  : `🔍 ${t('nova_campanha.detectar_business_btn')}`}
              </button>
            </div>
            <div className="row" style={{ flexWrap: 'wrap', gap: '6px' }}>
              {(() => {
                // Conta dentro do escopo da instância selecionada (ou todos, se
                // nenhuma estiver filtrada) — assim os números batem com a lista exibida.
                const base = filtroInstancia ? contatos.filter(c => c.instancia === filtroInstancia) : contatos;
                const qtdBusiness = base.filter(c => c.is_business === true).length;
                const qtdPessoal  = base.filter(c => c.is_business !== true).length;
                const opcoes = [
                  { id: 'todos',            label: t('nova_campanha.filtro_business_todos'),            qtd: base.length },
                  { id: 'somente_business', label: t('nova_campanha.filtro_business_somente_business'), qtd: qtdBusiness, icone: '🏢' },
                  { id: 'somente_pessoal',  label: t('nova_campanha.filtro_business_somente_pessoal'),  qtd: qtdPessoal,  icone: '👤' },
                ];
                return opcoes.map(op => {
                  const ativo = filtroBusiness === op.id;
                  return (
                    <button
                      key={op.id}
                      onClick={() => setFiltroBusiness(op.id)}
                      className={`pill${ativo ? ' is-active' : ''}`}
                      style={ativo
                        ? { background: '#13305f', borderColor: '#13305f', color: '#fff' }
                        : { borderColor: '#86efac', color: '#13305f' }}
                    >
                      {op.icone ? `${op.icone} ` : ''}{op.label}
                      <span style={{ marginLeft: '4px', opacity: 0.75, fontSize: '11px' }}>({op.qtd})</span>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Filtro por tag */}
          {tagsDisponiveis.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('nova_campanha.filtro_tags_secao')}
              </p>
              <div className="row" style={{ flexWrap: 'wrap', gap: '6px' }}>
                <button
                  onClick={() => setFiltroTag('')}
                  className={`pill${!filtroTag ? ' is-active' : ''}`}
                  style={!filtroTag
                    ? { background: '#13305f', borderColor: '#13305f', color: '#fff' }
                    : { borderColor: '#86efac', color: '#13305f' }}
                >
                  {t('nova_campanha.filtro_tags_todos')}
                </button>
                {tagsDisponiveis.map(tag => {
                  const qtd = (filtroInstancia ? contatos.filter(c => c.instancia === filtroInstancia) : contatos)
                    .filter(c => Array.isArray(c.tags) && c.tags.includes(tag)).length;
                  const ativo = filtroTag === tag;
                  return (
                    <button key={tag} onClick={() => setFiltroTag(ativo ? '' : tag)}
                      className={`pill${ativo ? ' is-active' : ''}`}
                      style={ativo
                        ? { background: '#13305f', borderColor: '#13305f', color: '#fff' }
                        : { borderColor: '#86efac', color: '#13305f' }}
                    >
                      {tag}
                      <span style={{ marginLeft: '4px', opacity: 0.75, fontSize: '11px' }}>({qtd})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="row" style={{ marginBottom: '1.25rem' }}>
            <button onClick={selecionarTodos} className="btn btn-secondary btn-sm">
              {todosSelecionados ? <CheckSquare size={15} /> : <Square size={15} />}
              {todosSelecionados
                ? (filtroInstancia ? t('nova_campanha.btn_desmarcar_instancia', { inst: filtroInstancia }) : t('nova_campanha.btn_desmarcar_todos'))
                : (filtroInstancia ? t('nova_campanha.btn_selecionar_instancia', { inst: filtroInstancia }) : t('nova_campanha.btn_selecionar_todos'))}
            </button>

            <label className="btn btn-sm" style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac', cursor: 'pointer' }}>
              <Upload size={15} />
              {importando ? t('nova_campanha.btn_importando') : t('nova_campanha.btn_importar_csv')}
              <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
            </label>

            <button onClick={() => setMostrarFormContato(!mostrarFormContato)} className="btn btn-info btn-sm">
              {t('nova_campanha.btn_contato_manual')}
            </button>

            <span className="muted" style={{ fontSize: '13px', marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Users size={14} />
              {filtroInstancia
                ? t('nova_campanha.contadores_filtrado', { sel: contatosFiltrados.filter(c => contatosSelecionados.includes(c.id)).length, total: contatosFiltrados.length, inst: filtroInstancia })
                : t('nova_campanha.contadores_geral', { sel: contatosSelecionados.length, total: contatos.length })}
            </span>
          </div>

          <div className="info-box" style={{ marginBottom: '1rem', fontSize: '12px' }}>
            {t('nova_campanha.csv_hint')}
          </div>

          {mostrarFormContato && (
            <div style={{ background: '#f8faff', borderRadius: 'var(--radius)', padding: '14px', marginBottom: '1rem', border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8', marginBottom: '12px' }}>{t('nova_campanha.form_contato_titulo')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                {[
                  [t('nova_campanha.form_contato_nome'), 'nome', 'text'],
                  [t('nova_campanha.form_contato_telefone'), 'telefone', 'text'],
                  [t('nova_campanha.form_contato_email'), 'email', 'email']
                ].map(([label, name, type]) => (
                  <div key={name}>
                    <label className="label" style={{ fontSize: '12px' }}>{label}</label>
                    <input type={type} value={novoContato[name]}
                      onChange={e => setNovoContato({ ...novoContato, [name]: e.target.value })}
                      className="input input-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="row">
                <button onClick={handleAdicionarContato} disabled={salvandoContato} className="btn btn-primary btn-sm">
                  {salvandoContato ? t('nova_campanha.form_contato_salvando') : t('nova_campanha.form_contato_salvar')}
                </button>
                <button onClick={() => setMostrarFormContato(false)} className="btn btn-secondary btn-sm">
                  {t('nova_campanha.form_contato_cancelar')}
                </button>
              </div>
            </div>
          )}

          {importados.length > 0 && (() => {
            const ids = importados.map(c => c.id);
            const marcados = ids.filter(id => contatosSelecionados.includes(id)).length;
            const todosMarcados = marcados === ids.length;
            return (
              <div className="info-box info-box-success" style={{ padding: '12px 14px', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>
                    {t('nova_campanha.importados_titulo', { n: importados.length, sel: marcados })}
                  </p>
                  <div className="row-tight">
                    <button onClick={toggleTodosImportados} className="btn btn-sm" style={{ background: '#fff', borderColor: '#86efac', color: 'var(--color-success)' }}>
                      {todosMarcados ? t('nova_campanha.importados_desmarcar') : t('nova_campanha.importados_selecionar')}
                    </button>
                    <button onClick={() => setImportados([])} title={t('nova_campanha.importados_fechar')} className="btn btn-sm btn-secondary">×</button>
                  </div>
                </div>
                <div style={{ maxHeight: '220px', overflowY: 'auto', background: '#fff', borderRadius: 'var(--radius-sm)', border: '1px solid #d1fae5' }}>
                  {importados.map(c => (
                    <label key={c.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--color-divider)',
                      background: contatosSelecionados.includes(c.id) ? '#f0fdf4' : '#fff'
                    }}>
                      <input type="checkbox"
                        checked={contatosSelecionados.includes(c.id)}
                        onChange={() => toggleContato(c.id)}
                        style={{ accentColor: 'var(--color-success)' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.nome || '—'}</div>
                        <div className="muted" style={{ fontSize: '12px' }}>{c.telefone} {c.email ? `· ${c.email}` : ''}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Busca */}
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <input
              type="text"
              value={buscaContato}
              onChange={e => setBuscaContato(e.target.value)}
              placeholder={t('nova_campanha.busca_placeholder')}
              className="input input-sm"
              style={{ paddingLeft: '32px' }}
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none', fontSize: '14px' }}>🔍</span>
            {buscaContato && (
              <button onClick={() => setBuscaContato('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', lineHeight: 1 }}>✕</button>
            )}
          </div>

          <div style={{ maxHeight: '340px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
            {contatos.length === 0 ? (
              <div className="empty">
                {t('nova_campanha.vazio_nenhum_contato')}
              </div>
            ) : contatosFiltrados.length === 0 ? (
              <div className="empty">
                {filtroInstancia && !buscaContato
                  ? t('nova_campanha.vazio_filtro_instancia', { inst: filtroInstancia })
                  : t('nova_campanha.vazio_busca')}
              </div>
            ) : (
              contatosFiltrados.map(c => (
                <div key={c.id} onClick={() => toggleContato(c.id)} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--color-divider)',
                  background: contatosSelecionados.includes(c.id) ? 'var(--color-accent-soft)' : 'var(--color-surface)'
                }}>
                  {contatosSelecionados.includes(c.id)
                    ? <CheckSquare size={16} color="var(--color-accent)" />
                    : <Square size={16} color="#ccc" />
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>{c.nome || '—'}</div>
                    <div className="muted" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span>{c.telefone}{c.email ? ` · ${c.email}` : ''}</span>
                      {c.instancia && (
                        <span style={{ fontSize: '10px', background: '#dcfce7', color: '#13305f', borderRadius: '3px', padding: '1px 5px' }}>
                          {c.instancia}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="row" style={{ marginTop: '1.25rem' }}>
            <button onClick={() => setEtapa(1)} className="btn btn-secondary">
              {t('nova_campanha.btn_voltar')}
            </button>
            <button onClick={handleSubmit} disabled={carregando} className="btn btn-primary" style={{ flex: 1, padding: '12px' }}>
              {carregando ? t('nova_campanha.btn_criando') : t('nova_campanha.btn_criar_campanha', { n: totalSelecionados })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
